import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Tooltip } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { Spin, SuccessCard, WarningCard } from 'common/components';
import { ERoute } from 'common/const';
import { showMessage } from 'common/helpers';
import { AuthHooks } from 'common/hooks';
import { LIST_LIMIT_0, LIST_LIMIT_20 } from 'common/config';
import { ArrowDownShort, Bin, Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { BasketMoreButton, BasketProgress, BasketOrderDetails, BasketGoodsList } from 'entities/Basket/components';
import {
  filterBasketGoods,
  mapBasketGoodsToPayload,
  mapGoodToBasketGood,
  updateBasketGoodCount,
} from 'entities/Basket/Basket.helper';
import { IBasket, IDeleteBasketGoodPayload, IBasketGoods, ICreateBasketRequestPayload } from 'entities/Basket/Basket.models';
import { IGood } from 'entities/Goods/Goods.models';
import { EUserRole } from 'entities/User/User.enum';
import {
  createBasketRequest,
  getBasketDelivery,
  getBasketStores,
  getCurrentBasket,
  selectCurrentBasket,
  selectIsBasketLoading,
  updateBasket,
} from 'entities/Basket/Basket.slice';
import { getLegalList } from 'entities/LegalEntity/Legal.slice';
import { getSubdivisionById, getSubdivisionList } from 'entities/Subdivision/Subdivision.slice';
import { getUserAssignees, getUserList, getUserStats, selectCurrentUser } from 'entities/User/User.slice';
import { getGoodList, selectIsGoodLoading } from 'entities/Goods/Good.slice';
import { SelectGoodCard } from 'entities/Goods/components';
import { SelectCityModal } from 'entities/City/components';
import { CityHooks } from 'entities/City/City.hooks';

type ActiveModalType = 'selectGoodCard' | 'successCard' | 'warningCard';

export const BasketPage: FC = () => {
  const dispatch = useAppDispatch();

  const currentBasket = useAppSelector(selectCurrentBasket);
  const isBasketLoading = useAppSelector(selectIsBasketLoading);
  const isGoodLoading = useAppSelector(selectIsGoodLoading);
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [fetchDeliveryLoading, setFetchDeliveryLoading] = useState<boolean>(false);
  const [goods, setGoods] = useState<IBasketGoods[]>([]);
  const [selectedGoods, setSelectedGoods] = useState<IBasketGoods[]>([]);
  const [isUIUpdateLoading, setIsUIUpdateLoading] = useState<boolean>(false);
  const [isUpdateCountLoading, setIsUpdateCountLoading] = useState<boolean>(false);
  const [isUpdateSelectionLoading, setIsUpdateSelectionLoading] = useState<boolean>(false);
  const [activeModalType, setActiveModalType] = useState<ActiveModalType | null>(null);
  const navigate = useNavigate();

  const { isSeller } = AuthHooks.useIsSeller();
  const { openSelectCityModal, props: selectCityModalProps } = CityHooks.useSelectCityModal();

  const basketId = currentUser?.basketId;
  const checkAll = goods.length > 0 && goods.length === selectedGoods.length;
  const indeterminate = selectedGoods.length > 0 && selectedGoods.length < goods.length;
  const listLoading = fetchLoading || isGoodLoading || isUpdateCountLoading || isUpdateSelectionLoading;

  const getDeliveryDetails = async (
    goods: IBasketGoods[],
    params?: { basket?: IBasket | null; userCityId?: number },
  ): Promise<void> => {
    const basket = params?.basket ?? currentBasket;
    const userCityId = params?.userCityId ?? currentUser?.city?.id;

    if (!basket?.account.isSelfRegistered || !goods.length) {
      return;
    }

    setFetchDeliveryLoading(true);

    const goodsPayload = mapBasketGoodsToPayload(goods);

    if (userCityId) {
      await dispatch(getBasketStores({ cityId: userCityId, goods: goodsPayload }));
    }

    if (basket.address?.cityId) {
      await dispatch(getBasketDelivery({ cityId: basket.address.cityId, goods: goodsPayload }));
    }

    setFetchDeliveryLoading(false);
  };

  const fetchData = async (): Promise<void> => {
    if (!basketId) {
      setFetchLoading(false);
      return;
    }

    const basket = await dispatch(getCurrentBasket(basketId));

    if (!basket) {
      setFetchLoading(false);
      return;
    }

    await dispatch(
      getSubdivisionList({
        userId: basket.user.id,
        limit: LIST_LIMIT_0,
      }),
    );

    if (basket.subdivisionId) {
      await dispatch(getSubdivisionById({ id: basket.subdivisionId }));
    }

    await dispatch(getLegalList({ limit: LIST_LIMIT_0 }));

    const availableGoods = filterBasketGoods.getAvailableForCustomerGoods(basket.goods, basket.maxGoodPrice);

    await getDeliveryDetails(availableGoods, { basket });

    if (basket.account.isSelfRegistered) {
      await dispatch(getUserAssignees({ limit: 0 }));
      await dispatch(getUserList({ subdivisionId: basket.subdivisionId, limit: 0 }));
    }

    setGoods(basket.goods);
    setSelectedGoods(availableGoods);
    setFetchLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [basketId, currentUser?.city?.id]);

  const debouncedUpdateBasketGoods = useMemo(
    () =>
      debounce(async (goods: IBasketGoods[], selectedGoods: IBasketGoods[]) => {
        if (!currentBasket) {
          setIsUIUpdateLoading(false);
          return;
        }

        setIsUpdateCountLoading(true);

        try {
          const response = await dispatch(updateBasket({ id: currentBasket.id, goods: mapBasketGoodsToPayload(goods) }));

          if (response) {
            const availableGoods = filterBasketGoods.getAvailableForCustomerGoods(response.goods, response.maxGoodPrice);
            const availableGoodIds = new Set(availableGoods.map((i) => i.goodId));
            const newSelectedGoods = selectedGoods.filter((item) => availableGoodIds.has(item.goodId));

            await getDeliveryDetails(newSelectedGoods, { basket: response });
          }
        } finally {
          setIsUIUpdateLoading(false);
          setIsUpdateCountLoading(false);
        }
      }, 800),
    [currentBasket],
  );

  useEffect(() => {
    return () => {
      debouncedUpdateBasketGoods.cancel();
    };
  }, [debouncedUpdateBasketGoods]);

  const changeGoodCount = (id: number, count: number): void => {
    const newGoods = updateBasketGoodCount(goods, id, count);
    const newSelectedGoods = updateBasketGoodCount(selectedGoods, id, count);

    setIsUIUpdateLoading(true);
    setGoods(newGoods);
    setSelectedGoods(newSelectedGoods);
    debouncedUpdateBasketGoods(newGoods, newSelectedGoods);
  };

  const changeCheckAll = async (e: CheckboxChangeEvent): Promise<void> => {
    if (indeterminate) {
      setSelectedGoods([]);
      return;
    }

    setIsUpdateSelectionLoading(true);

    if (e.target.checked) {
      const availableGoods = filterBasketGoods.getAvailableForCustomerGoods(goods, currentBasket?.maxGoodPrice);

      setSelectedGoods(availableGoods);
      await getDeliveryDetails(availableGoods);
    } else {
      setSelectedGoods([]);
    }

    setIsUpdateSelectionLoading(false);
  };

  const changeGoodSelection = async (good: IBasketGoods, checked: boolean): Promise<void> => {
    const newSelectedGoods = checked ? [...selectedGoods, good] : filterBasketGoods.notEqualId(selectedGoods, good.goodId);

    setIsUpdateSelectionLoading(true);
    setSelectedGoods(newSelectedGoods);
    await getDeliveryDetails(newSelectedGoods);
    setIsUpdateSelectionLoading(false);
  };

  const deleteGood = ({ id, onSuccess }: IDeleteBasketGoodPayload): void => {
    if (currentBasket && id) {
      const filteredGoods = filterBasketGoods.notEqualId(goods, id);

      dispatch(
        updateBasket({
          id: currentBasket.id,
          goods: mapBasketGoodsToPayload(filteredGoods),
          onSuccess: async () => {
            const filteredSelectedGoods = filterBasketGoods.notEqualId(selectedGoods, id);

            setGoods(filteredGoods);
            setSelectedGoods(filteredSelectedGoods);
            onSuccess?.();
            showMessage({ content: 'Товар удален из корзины.', icon: <Bin className="icon-bin-dark-grey" /> });
            await getDeliveryDetails(filteredSelectedGoods);
            dispatch(getUserStats());
          },
        }),
      );
    }
  };

  const openSelectGoodCard = async (): Promise<void> => {
    await dispatch(
      getGoodList({
        goodInBasket: false,
        limit: LIST_LIMIT_20,
        offset: 0,
      }),
    );
    setActiveModalType('selectGoodCard');
  };

  const addGoodToBasket = (good: IGood, onSuccess?: () => void): void => {
    if (currentBasket) {
      const basketGood = mapGoodToBasketGood(good);
      const newGoods = [...goods, basketGood];

      dispatch(
        updateBasket({
          id: currentBasket.id,
          goods: mapBasketGoodsToPayload(newGoods),
          onSuccess: async () => {
            const [availableGood] = filterBasketGoods.getAvailableForCustomerGoods([basketGood], currentBasket.maxGoodPrice);
            const newSelectedGoods = availableGood ? [...selectedGoods, availableGood] : selectedGoods;

            setGoods(newGoods);
            setSelectedGoods(newSelectedGoods);
            onSuccess?.();
            await getDeliveryDetails(newSelectedGoods);
            dispatch(getUserStats());
          },
        }),
      );
    }
  };

  const clearBasket = (): void => {
    if (currentBasket) {
      dispatch(
        updateBasket({
          id: currentBasket.id,
          goods: [],
          onSuccess: async () => {
            setGoods([]);
            setSelectedGoods([]);
            setActiveModalType(null);
            showMessage({ content: 'Все товары удалены из корзины.', icon: <Bin className="icon-bin-dark-grey" /> });
            dispatch(getUserStats());
          },
        }),
      );
    }
  };

  const createRequest = (payload: Omit<ICreateBasketRequestPayload, 'goods'>): void => {
    dispatch(
      createBasketRequest({
        ...payload,
        goods: mapBasketGoodsToPayload(selectedGoods),
        onSuccess: () => {
          payload.onSuccess?.();
          setActiveModalType('successCard');

          const selectedGoodIds = new Set(selectedGoods.map((i) => i.goodId));

          setGoods((prev) => prev.filter((i) => !selectedGoodIds.has(i.goodId)));
          setSelectedGoods([]);
          dispatch(getUserStats());
        },
      }),
    );
  };

  const updateGoods = (basket: IBasket, userCityId?: number): void => {
    const availableGoods = filterBasketGoods.getAvailableForCustomerGoods(basket.goods, basket.maxGoodPrice);
    const availableGoodIds = new Set(availableGoods.map((i) => i.goodId));
    const newSelectedGoods = selectedGoods.filter((item) => availableGoodIds.has(item.goodId));

    setGoods(basket.goods);
    setSelectedGoods(newSelectedGoods);
    getDeliveryDetails(newSelectedGoods, { basket, userCityId });
  };

  const navigateToRequests = (): void => navigate(`${ERoute.RequestList}/${EUserRole.User}`);

  const openWarningCard = (): void => setActiveModalType('warningCard');

  const closeModal = (): void => setActiveModalType(null);

  return (
    <>
      <div className="redesign h-full flex">
        <div className="pt-11 px-11 flex direction-column flex-1">
          <div className="mb-14 flex align-center justify-end">
            {!isSeller && (
              <Button className="catalog-header-city-selector mr-4" onClick={openSelectCityModal}>
                <span className="ff-regular fs-xs color-black">{currentUser?.city?.name ?? 'Выберите город'}</span>
                <ArrowDownShort className="icon-dark-grey" />
              </Button>
            )}

            <Tooltip overlayClassName="redesign" title="Быстрое добавление товара">
              <Button
                className="button-circle primary"
                icon={<Plus className="icon-plus-white" />}
                onClick={openSelectGoodCard}
              />
            </Tooltip>

            <div className="ml-4">
              <BasketMoreButton id={currentBasket?.id} clearDisabled={!currentBasket?.goods.length} onDelete={openWarningCard} />
            </div>
          </div>

          <div className="relative px-11 flex direction-column flex-1">
            <div className="relative mb-13">
              <span className="ff-regular fs-xxl">Корзина</span>

              <span className="absolute ml-2 ff-regular fs-l" style={{ top: 0 }}>
                {currentBasket?.goods.length ?? 0}
              </span>
            </div>

            {!!goods.length && (
              <div className="mb-5 ml-3">
                <Checkbox checked={checkAll} indeterminate={indeterminate} onChange={changeCheckAll}>
                  Включить в заказ
                </Checkbox>
              </div>
            )}

            <Spin spinning={listLoading}>
              <BasketGoodsList
                basket={currentBasket}
                goods={goods}
                selectedGoods={selectedGoods}
                loading={isBasketLoading}
                onCountChange={changeGoodCount}
                onSelectGood={changeGoodSelection}
                onDeleteGood={deleteGood}
              />
            </Spin>

            {currentBasket?.availableLimit !== undefined && (
              <BasketProgress basket={currentBasket} selectedGoods={selectedGoods} />
            )}
          </div>
        </div>

        {!!goods.length && (
          <BasketOrderDetails
            basket={currentBasket}
            selectedGoods={selectedGoods}
            loading={isUIUpdateLoading || fetchDeliveryLoading}
            onGoodsUpdate={updateGoods}
            onCreateRequest={createRequest}
          />
        )}
      </div>

      <SelectCityModal {...selectCityModalProps} />

      <SelectGoodCard
        open={activeModalType === 'selectGoodCard'}
        loading={isBasketLoading}
        goodInBasket={false}
        onCancel={closeModal}
        onSelect={addGoodToBasket}
      />

      <SuccessCard
        open={activeModalType === 'successCard'}
        content={`Заказ от ${dayjs().format('DD.MM.YYYY')} в очереди на рассмотрение`}
        subtitle="Отправлено!"
        btnTitle="К моим заказам"
        onClose={closeModal}
        onConfirm={navigateToRequests}
      />

      <WarningCard
        open={activeModalType === 'warningCard'}
        content="Вы уверены, что хотите очистить корзину?"
        subtitle="Все товары будут безвозвратно удалены."
        confirmBtnTitle="Очистить"
        loading={isBasketLoading}
        onClose={closeModal}
        onConfirm={clearBasket}
      />
    </>
  );
};
